import React, { ReactElement, useEffect, useState, useTransition } from 'react';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { initialSetPasswordFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageSubTitle, StyledPageTitle } from 'theme/styles';
import { useAuth } from 'models/authContext';
import { ActionTypes } from 'state/actions/alert';
import { useAlert } from 'models/alertContext';
import { IData } from 'pages/AuthPage';
import { schema } from './schemaValidation';

interface ISetPasswors {
  oldPassword: string;
  type: string;
  changePage: (p: number) => void;
  setData: (IData: any) => void;
}

const SetPasswordPage = ({
  oldPassword,
  type,
  changePage,
  setData,
}: ISetPasswors): ReactElement => {
  const { t } = useTranslations();
  const [isPending, startTransition] = useTransition();
  const [isCreatePasswordSuccessfully, setIsCreateResetPasswordSuccessfully] = useState<
    boolean | null
  >(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialSetPasswordFormValues,
    ...(isCreatePasswordSuccessfully === null ? { resolver: yupResolver(schema) as any } : {}),
  });
  const { setPassword, authMessage, clearAuthErrors, accessToken } = useAuth();
  const { authAlert, clearAlert, setAlert } = useAlert();

  useEffect(
    () => () => {
      clearAuthErrors();
    },
    [],
  );

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_AUTH_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setAlertCrateSuccess = (text: string) => {
    setAlert(ActionTypes.SET_AUTH_ALERT, {
      text,
      variant: EAlertVariants.success,
    });
  };

  const handleChangePasswordSubmit = handleSubmit(async (formData) => {
    startTransition(() => {
      const body = { oldPassword, newPassword: formData.newPassword, accessToken };
      setPassword(body, (res: any) => {
        if (res) {
          setData((data: IData) => [{ ...data, password: '' }]);
          setAlertCrateSuccess(t('page.setPassword.success.alert'));
          setIsCreateResetPasswordSuccessfully(true);
        }
      });
    });
  });

  const renderSetPasswordForm = () => (
    <Grid item container xs={12} gap={3}>
      <Grid item xs={12} gap={2}>
        <Grid item xs={12}>
          <FormInput
            name="newPassword"
            type={EFormInputTypes.password}
            label={t('page.setPassword.input.newPassword')}
            placeholder={t('page.setPassword.input.newPassword')}
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12}>
          <FormInput
            name="retypeNewPassword"
            type={EFormInputTypes.password}
            label={t('page.setPassword.input.retypeNewPassword')}
            placeholder={t('page.setPassword.input.retypeNewPassword')}
            control={control}
            errors={errors}
          />
        </Grid>
      </Grid>

      <Grid item container xs={12} gap={3}>
        <Grid item container xs={12} gap={1.3} justifyItems="center">
          <Grid item xs={12}>
            <Button
              id="confirm"
              label={t('page.setPassword.button.confirm')}
              onClick={handleChangePasswordSubmit}
              isLoading={isPending}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              id="start-page"
              label={t('page.setPassword.button.cancel')}
              variant={EButtonVariants.outlined}
              isLoading={isPending}
              fullWidth
              onClick={() => changePage(1)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderSetPasswordHeaderForm = () => (
    <Grid item container xs={12} gap={1}>
      <Grid item xs={12}>
        <StyledPageTitle variant="h4">{t('page.setPassword.title')}</StyledPageTitle>
      </Grid>
      <Grid item xs={12}>
        <StyledPageSubTitle variant="body2">
          {t('page.setPassword.subtitle')}
          <li>
            <Typography variant="caption">{t('page.setPassword.subtitle.point1')}</Typography>
          </li>
          <li>
            <Typography variant="caption">{t('page.setPassword.subtitle.point2')}</Typography>
          </li>
        </StyledPageSubTitle>
      </Grid>
    </Grid>
  );

  const renderSetPasswordSuccessTemplate = () => (
    <Grid item container xs={12} gap={1}>
      <Grid item xs={12}>
        <StyledPageTitle variant="h4">
          {t(`page.setPassword.${type}.success.title`)}
        </StyledPageTitle>
      </Grid>
      <Grid item xs={12}>
        <StyledPageSubTitle variant="body2">
          {t('page.setPassword.success.subtitle')}
        </StyledPageSubTitle>
      </Grid>
    </Grid>
  );

  return (
    <AuthTemplate>
      {isCreatePasswordSuccessfully
        ? renderSetPasswordSuccessTemplate()
        : renderSetPasswordHeaderForm()}

      <Grid item container xs={12} gap={7}>
        {authAlert && (
          <Grid item xs={12} mb={2}>
            <Alert text={authAlert.text} variant={authAlert.variant} />
          </Grid>
        )}
        {authMessage.text && !authAlert && (
          <Grid item xs={12} mb={2}>
            <Alert text={authMessage.text} variant={authMessage.variant} />
          </Grid>
        )}

        {!isCreatePasswordSuccessfully ? (
          renderSetPasswordForm()
        ) : (
          <Grid item xs={12} container>
            <Button
              id="start-page"
              label={t('page.setPassword.button.login')}
              variant={EButtonVariants.contained}
              fullWidth
              onClick={() => changePage(2)}
            />
          </Grid>
        )}
      </Grid>
    </AuthTemplate>
  );
};
export { SetPasswordPage };
